/**
 * FollowUpRequestsTab — calendar-level tab listing follow-up
 * appointment requests. Mirrors Flutter `fu_requests_tab.dart` (991
 * lines). PR-1 of 3 (PROD-847): list + search + paging + archive
 * toggle + loading/empty/error states. PR-2 will add the Cancel
 * Request kebab + cancellation-reason dialog; PR-3 will add Send
 * Reminder (single-row + bulk).
 *
 * Wire shape verified against 2026-05-20 dev curl batch — see
 * `docs/CONVERSIONS/fu-requests/CONVERSION_NOTES.md`.
 *
 * Doctor-role client-side filter (Flutter
 * `fu_requests_tab.dart:543-546`): when `user.role === '3'`, drop
 * rows whose `provider.id` doesn't match `user.userId`. Server
 * doesn't apply this filter — it's a per-Flutter convention to
 * scope a doctor's view to their own follow-ups.
 */

import { getTimeFormat } from '@/shared/lib/time-format';
import { useMemo, useState } from 'react';
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  CalendarPlus,
  Search,
  Send,
  X,
} from 'lucide-react';
import { BridgeStatusChip } from '@/components/ui/status-chip';
import { Input } from '@/components/ui/input';
import { RowActions, type RowAction } from '@/components/blocks/row-actions';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/auth/auth-store';
import { usePermission } from '@/auth/permission-gate';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { ListPaginationFooter } from '@/shared/ui/molecules/list-pagination-footer';
import { useFollowUpRequestList } from '@/scheduling/services/use-follow-up-request-list';
import {
  CancelFuRequestDialog,
  type CancelFuRequestTarget,
} from './cancel-fu-request-dialog';
import {
  SendReminderConfirmDialog,
  type SendReminderTarget,
} from './send-reminder-confirm-dialog';
import { Checkbox } from '@/components/ui/checkbox';
import type { FuRequestResponse } from '@/types/scheduling-api';
import { DOCTOR_ROLE } from '@/auth/roles';

const PAGE_SIZES = [10, 20, 50] as const;

/**
 * Sortable column keys. Maps to the 8 table headers. Flutter's
 * `sortForm` (`fu_requests_tab.dart:359-456`) is client-side over
 * `requestList` (the current page), NOT a server refetch — so the
 * sort affects only the rows on screen.
 *
 * Sort is BY COLUMN NAME, not column index (Samer PR #268 review §3).
 * `compareRows` switches on this string union; `SortableTh` passes
 * the same string back via `onSortColumn`. Even if a column is
 * hidden (e.g. the kebab column on Cancelled view), the sort key
 * mapping is unaffected — no index-shift bug possible.
 */
type SortColumn =
  | 'requestedOn'
  | 'reasonCode'
  | 'patient'
  | 'contactNumber'
  | 'provider'
  | 'followUpIn'
  | 'lastReminder'
  | 'status';

type SortDirection = 'asc' | 'desc';

interface SortState {
  column: SortColumn;
  direction: SortDirection;
}

/**
 * Props for FollowUpRequestsTab.
 *
 * `onCreateFollowUp` is the FU-tab-specific callback that, when fired
 * from a row's kebab, signals the parent to open
 * `CreateAppointmentPanel` pre-populated with the row's patient +
 * provider. Mirrors Flutter `fu_requests_tab.dart:35,41,469-473`'s
 * `createFollowUpAppointment` callback. Optional: when omitted, the
 * "Create Follow Up" menu item is hidden entirely (e.g. story
 * fixtures that don't wire the parent panel).
 */
interface FollowUpRequestsTabProps {
  onCreateFollowUp?: (row: FuRequestResponse) => void;
}

export function FollowUpRequestsTab({
  onCreateFollowUp,
}: FollowUpRequestsTabProps) {
  const organizationId = useAuthStore((s) => s.user?.organizationId);
  const practiceId = useAuthStore((s) => s.user?.practiceId);
  const userRole = useAuthStore((s) => s.user?.role);
  const userId = useAuthStore((s) => s.user?.userId);
  // Flutter parity — `fu_requests_tab.dart:469-473` gates the
  // "Create Follow Up" menu item on `validatePermission([Doctor_Calender],
  // …, add: true)`. Magic-string `Doctor_Calender` is the verbatim
  // Flutter key including the original typo (per CLAUDE.md
  // magic-strings rule).
  const canCreateFollowUp = usePermission(['Doctor_Calender'], { add: true });

  const [searchInput, setSearchInput] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState<(typeof PAGE_SIZES)[number]>(10);
  const [sort, setSort] = useState<SortState | null>(null);
  const [cancelTarget, setCancelTarget] = useState<CancelFuRequestTarget | null>(null);
  // Selection state — Set<rowId>, NOT Set<index>. Flutter uses indexes
  // (`selectedFormIndexes: List<int>`) but indexes are fragile under
  // sort + filter + paging: a stable Set<string> avoids the
  // index-shift trap completely.
  const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(new Set());
  // Send Reminder dialog state — drives both kebab single-row and
  // bulk-bar multi-row flows. `null` = closed.
  const [reminderTarget, setReminderTarget] = useState<SendReminderTarget | null>(null);

  // Flutter parity — search input debounces 500ms before refetch
  // (`fu_requests_tab.dart:767-783`).
  const debouncedSearch = useDebounce(searchInput, 500);

  const { data, isLoading, isError } = useFollowUpRequestList({
    filter: {
      organizationId: organizationId ?? '',
      practiceId: practiceId ?? '',
      searchText: debouncedSearch,
      pageNumber,
      pageSize,
      isActive,
    },
  });

  // Doctor-role client-side filter — applied AFTER server response,
  // BEFORE render. Server returns all rows; the role-3 check is
  // client-side per Flutter `fu_requests_tab.dart:543-546`.
  //
  // Known caveat (Samer PR #266 review §2): because the filter
  // operates on a paged response, a doctor on a page with 20 rows
  // may see only the rows that belong to them — e.g. 3 visible from
  // a server page of 20. The pagination footer's `totalRecords`
  // reflects the SERVER count (all roles), but `visibleRows.length`
  // reflects only the doctor's subset.
  //
  // We surface this honestly in the header ("Showing X of Y" when
  // the filter is active) AND show a banner explaining why some
  // rows are hidden. The backend's `FuRequestFilter` currently has
  // no `providerId` field — if/when the API adds one, push the
  // filter server-side to fix the pagination skew.
  const allRows = data?.fuRequests ?? [];
  const isDoctorFiltering = userRole === DOCTOR_ROLE && !!userId;
  const filteredRows = useMemo(() => {
    if (isDoctorFiltering) {
      return allRows.filter((r) => r.provider?.id === userId);
    }
    return allRows;
  }, [allRows, isDoctorFiltering, userId]);
  /**
   * Client-side sort over the current page's rows (Flutter parity —
   * `sortForm` does NOT trigger a server refetch). Returns a new
   * array to keep React-key stability on re-render.
   */
  const visibleRows = useMemo(() => {
    if (!sort) return filteredRows;
    const dir = sort.direction === 'asc' ? 1 : -1;
    return [...filteredRows].sort(
      (a, b) => dir * compareRows(a, b, sort.column),
    );
  }, [filteredRows, sort]);

  /**
   * Cycle column sort on header click: unsorted → asc → desc → unsorted.
   * Clicking a different column starts fresh at asc.
   */
  const toggleSort = (column: SortColumn) => {
    setSort((prev) => {
      if (!prev || prev.column !== column) return { column, direction: 'asc' };
      if (prev.direction === 'asc') return { column, direction: 'desc' };
      return null;
    });
  };

  const toggleArchive = (active: boolean) => {
    if (active === isActive) return;
    setIsActive(active);
    setPageNumber(1); // reset to first page on view change
    setSelectedRowIds(new Set()); // selection clears on view-toggle (Flutter `:191`)
    // Defense-in-depth (Samer PR #277 review): both action dialogs are
    // triggerable ONLY on Active view, but clear their targets here
    // anyway so a view-toggle while a dialog is mid-close can't leave
    // a stale target hanging on the Cancelled view.
    setCancelTarget(null);
    setReminderTarget(null);
  };

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    setPageNumber(1); // reset to first page on new search
    setSelectedRowIds(new Set()); // selection clears on search change
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size as (typeof PAGE_SIZES)[number]);
    setPageNumber(1);
    setSelectedRowIds(new Set());
  };

  const handlePageChange = (next: number) => {
    setPageNumber(next);
    setSelectedRowIds(new Set()); // selection scoped to current page only
  };

  // Eligible-to-select rows (excludes Completed — matches kebab visibility
  // at Flutter `fu_requests_tab.dart:518-524`).
  const selectableRows = useMemo(
    () =>
      visibleRows.filter(
        (r) => (r.status ?? '').toLowerCase() !== 'completed',
      ),
    [visibleRows],
  );

  const toggleRowSelection = (id: string) => {
    setSelectedRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedRowIds((prev) => {
      const allSelected =
        selectableRows.length > 0 &&
        selectableRows.every((r) => prev.has(r.id));
      if (allSelected) return new Set();
      return new Set(selectableRows.map((r) => r.id));
    });
  };

  const unselectAll = () => setSelectedRowIds(new Set());

  const openBulkReminderDialog = () => {
    const ids = Array.from(selectedRowIds);
    if (ids.length === 0) return;
    setReminderTarget({ fuRequestIds: ids });
  };

  const openSingleReminderDialog = (row: FuRequestResponse) => {
    setReminderTarget({ fuRequestIds: [row.id] });
  };

  const selectionCount = selectedRowIds.size;
  // Bulk bar shows only on Active view (Flutter `:670` hides bulk
  // Send Reminder on archived/cancelled view) AND when ≥1 selected.
  // Bulk-select column itself follows the same gating.
  const showBulkSelect = isActive;
  const showBulkBar = showBulkSelect && selectionCount > 0;
  const allOnPageSelected =
    selectableRows.length > 0 &&
    selectableRows.every((r) => selectedRowIds.has(r.id));

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Header — swaps between the default toolbar (title + search) and
          the bulk-action bar when ≥1 selected. Flutter parity per
          `fu_requests_tab.dart:625-720`: when `selectedFormIndexes.length > 0`,
          the entire 60px header row is replaced with the selection bar. */}
      {showBulkBar ? (
        <div className="px-[var(--page-pad)] pt-[var(--page-header-gap)] pb-4">
          <div className="flex min-h-8 items-center gap-6 flex-wrap">
            <span className="text-sm font-semibold text-text-primary">
              {selectionCount} selected
            </span>
            <button
              type="button"
              onClick={unselectAll}
              className="text-sm font-semibold text-brand-primary hover:underline"
            >
              Unselect All
            </button>
            <button
              type="button"
              onClick={openBulkReminderDialog}
              className="text-sm font-semibold text-brand-primary hover:underline"
            >
              Send Reminder
            </button>
          </div>
        </div>
      ) : (
        <div className="px-[var(--page-pad)] pt-[var(--page-header-gap)] pb-4">
          <div className="flex items-center justify-end gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => toggleArchive(!isActive)}
                className="text-sm font-semibold text-brand-primary hover:underline"
              >
                View {isActive ? 'Cancelled' : 'Active'} Requests
              </button>

              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-muted pointer-events-none" />
                <Input
                  value={searchInput}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  placeholder="Search requests"
                  className="pl-8 w-56 h-8 text-sm"
                  aria-label="Search FU requests"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Doctor-role filter banner — see Samer PR #266 review §2 */}
      {isDoctorFiltering && !isLoading && !isError ? (
        <div className="mx-[var(--page-pad)] mb-4 rounded-[var(--radius-card)] px-4 py-2 bg-[var(--surface-new)] text-sm text-brand-depth">
          You're viewing only follow-up requests where you're the provider.
          Pages may show fewer rows than the page size suggests — the server
          paginates across all providers, then this view filters down to
          yours.
        </div>
      ) : null}

      {/* Body — the only scrolling region; the list is one card inset by the page padding
          (the list-table standard, src/index.css). `overflow: clip` on the card instead of the
          standard's `hidden`: a clipped card is not a scroll container, so the sticky header
          keeps sticking to this scroll region. `w-fit min-w-full`: a table wider than the
          tab grows the card with it, so the region scrolls sideways instead of clipping columns. */}
      <div className="flex-1 min-h-0 overflow-auto scrollbar-hide px-[var(--page-pad)] pb-[var(--page-pad)]">
        <section className="list-table w-fit min-w-full">
          <div className="list-table-title">
            <div className="flex items-baseline gap-2">
              <h2>
                {isActive ? 'Active Requests' : 'Cancelled Requests'}
              </h2>
              {data?.count != null ? (
                <p className="text-[length:var(--type-label-size)] font-normal text-text-secondary">
                  {isDoctorFiltering ? (
                    <>
                      Showing {visibleRows.length} of {allRows.length} on this page ·{' '}
                      {data.count} total
                    </>
                  ) : (
                    <>{data.count} total</>
                  )}
                </p>
              ) : null}
            </div>
          </div>
        {isLoading ? (
          <LoadingTable />
        ) : isError ? (
          <ErrorState />
        ) : visibleRows.length === 0 ? (
          <EmptyState />
        ) : (
          <RequestsTable
            rows={visibleRows}
            sort={sort}
            onSortColumn={toggleSort}
            // Kebab visibility — Flutter parity at
            // `fu_requests_tab.dart:518-524, :920`:
            //   - Cancelled view (`isActive === false`) → kebab empty,
            //     so we don't render it at all
            //   - Completed status → row shows NO kebab
            kebabEnabled={isActive}
            // Disable ALL kebab triggers while EITHER dialog is open
            // (Samer PR #268 review §4): without this guard, rapid
            // double-clicks across two different rows can queue a
            // stale target before React finishes rendering the dialog.
            kebabBusy={cancelTarget !== null || reminderTarget !== null}
            // Bulk-select column visibility — Flutter `:670` hides the
            // bulk "Send Reminder" link on archived/cancelled view;
            // by extension the checkboxes have no purpose there, so
            // hide the column entirely (saves horizontal space too).
            selectionEnabled={showBulkSelect}
            selectedRowIds={selectedRowIds}
            allOnPageSelected={allOnPageSelected}
            onToggleRow={toggleRowSelection}
            onToggleSelectAll={toggleSelectAll}
            onCancelRequest={(row) =>
              setCancelTarget({
                fuRequestId: row.id,
                patientFirstName: row.patient?.firstName,
              })
            }
            onSendReminder={openSingleReminderDialog}
            onCreateFollowUp={onCreateFollowUp}
            canCreateFollowUp={canCreateFollowUp}
          />
        )}
        </section>
      </div>

      {/* Footer */}
      {!isLoading && !isError && data ? (
        <ListPaginationFooter
          pageNumber={data.currentPage ?? pageNumber}
          pageSize={pageSize}
          pageSizes={PAGE_SIZES}
          totalRecords={data.count ?? 0}
          totalPages={data.totalPage ?? 1}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      ) : null}

      {/* Action dialogs — Cancel Request + Send Reminder.
          Conditionally mounted on `isActive` (Samer PR #277 review):
          both dialogs can ONLY be triggered from Active-view surfaces
          (kebab + bulk bar). Gating the mount itself — not just the
          `open` prop — keeps the Cancelled view DOM clean of dialog
          shells and mutation-hook subscriptions, consistent with how
          the checkbox column + kebab column are conditionally
          rendered (Flutter `:670` + `:920`). */}
      {isActive ? (
        <>
          <CancelFuRequestDialog
            open={cancelTarget !== null}
            onOpenChange={(open) => {
              if (!open) setCancelTarget(null);
            }}
            target={cancelTarget}
          />
          <SendReminderConfirmDialog
            open={reminderTarget !== null}
            onOpenChange={(open) => {
              if (!open) setReminderTarget(null);
            }}
            target={reminderTarget}
            onSuccess={() => {
              // Clear bulk selection so the toolbar swaps back to the
              // default search header after a successful bulk reminder.
              // Single-row reminder also calls this (no-op when set is
              // already empty — keeps the success handler symmetric).
              setSelectedRowIds(new Set());
            }}
          />
        </>
      ) : null}
    </div>
  );
}

// ── Table ───────────────────────────────────────────────────────────────

interface RequestsTableProps {
  rows: FuRequestResponse[];
  sort: SortState | null;
  onSortColumn: (col: SortColumn) => void;
  /** When false, kebab column is hidden entirely (Cancelled view). */
  kebabEnabled: boolean;
  /** When true, every kebab trigger is disabled (a row's cancel
   *  dialog is open) — prevents rapid-click race that could queue
   *  a stale cancelTarget across two rows. */
  kebabBusy: boolean;
  /** When true, render the bulk-select checkbox column (leftmost).
   *  Hidden on Cancelled view in lockstep with the bulk action bar. */
  selectionEnabled: boolean;
  selectedRowIds: Set<string>;
  /** True when every selectable row on the current page is selected —
   *  drives the header check-all checkbox indicator. */
  allOnPageSelected: boolean;
  onToggleRow: (id: string) => void;
  onToggleSelectAll: () => void;
  onCancelRequest: (row: FuRequestResponse) => void;
  onSendReminder: (row: FuRequestResponse) => void;
  /** PROD-920: optional Create Follow Up handler. When omitted, the
   *  menu item is hidden. */
  onCreateFollowUp?: (row: FuRequestResponse) => void;
  /** PROD-920: `Doctor_Calender` + add permission check resolved at
   *  the tab level via `usePermission`; threaded down so kebab
   *  render skips the menu item without the permission. */
  canCreateFollowUp: boolean;
}

function RequestsTable({
  rows,
  sort,
  onSortColumn,
  kebabEnabled,
  kebabBusy,
  selectionEnabled,
  selectedRowIds,
  allOnPageSelected,
  onToggleRow,
  onToggleSelectAll,
  onCancelRequest,
  onSendReminder,
  onCreateFollowUp,
  canCreateFollowUp,
}: RequestsTableProps) {
  return (
    <table className="w-full">
      <thead className="sticky top-0 z-10">
        <tr>
          {selectionEnabled ? (
            <th className="w-10 px-2 py-2">
              <Checkbox
                checked={allOnPageSelected}
                onCheckedChange={onToggleSelectAll}
                aria-label="Select all on page"
              />
            </th>
          ) : null}
          <SortableTh column="requestedOn" sort={sort} onSortColumn={onSortColumn}>Requested</SortableTh>
          <SortableTh column="reasonCode" sort={sort} onSortColumn={onSortColumn}>RFV</SortableTh>
          <SortableTh column="patient" sort={sort} onSortColumn={onSortColumn}>Patient</SortableTh>
          <SortableTh column="contactNumber" sort={sort} onSortColumn={onSortColumn}>Contact Number</SortableTh>
          <SortableTh column="provider" sort={sort} onSortColumn={onSortColumn}>Doctor</SortableTh>
          <SortableTh column="followUpIn" sort={sort} onSortColumn={onSortColumn}>Follow up in</SortableTh>
          <SortableTh column="lastReminder" sort={sort} onSortColumn={onSortColumn}>Last Reminder</SortableTh>
          <SortableTh column="status" sort={sort} onSortColumn={onSortColumn}>Status</SortableTh>
          {kebabEnabled ? <th aria-label="Actions" /> : null}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <RequestRow
            key={row.id}
            row={row}
            kebabEnabled={kebabEnabled}
            kebabBusy={kebabBusy}
            selectionEnabled={selectionEnabled}
            isSelected={selectedRowIds.has(row.id)}
            onToggleRow={onToggleRow}
            onCancelRequest={onCancelRequest}
            onSendReminder={onSendReminder}
            onCreateFollowUp={onCreateFollowUp}
            canCreateFollowUp={canCreateFollowUp}
          />
        ))}
      </tbody>
    </table>
  );
}

function RequestRow({
  row,
  kebabEnabled,
  kebabBusy,
  selectionEnabled,
  isSelected,
  onToggleRow,
  onCancelRequest,
  onSendReminder,
  onCreateFollowUp,
  canCreateFollowUp,
}: {
  row: FuRequestResponse;
  kebabEnabled: boolean;
  kebabBusy: boolean;
  selectionEnabled: boolean;
  isSelected: boolean;
  onToggleRow: (id: string) => void;
  onCancelRequest: (row: FuRequestResponse) => void;
  onSendReminder: (row: FuRequestResponse) => void;
  onCreateFollowUp?: (row: FuRequestResponse) => void;
  canCreateFollowUp: boolean;
}) {
  // Per-row kebab visibility — Flutter `fu_requests_tab.dart:518-524`:
  // Completed rows have NO kebab. Other statuses get one when the
  // tab is in Active view (`kebabEnabled` from caller).
  const isCompleted = (row.status ?? '').toLowerCase() === 'completed';
  const showKebab = kebabEnabled && !isCompleted;
  // Completed rows are not selectable — Flutter doesn't render a
  // checkbox-equivalent for them either (no kebab → no actions).
  const showCheckbox = selectionEnabled && !isCompleted;

  return (
    <tr className="hover:bg-grey-100/60">
      {selectionEnabled ? (
        <td className="px-2 py-2 align-middle">
          {showCheckbox ? (
            <Checkbox
              checked={isSelected}
              onCheckedChange={() => onToggleRow(row.id)}
              aria-label={`Select request ${row.id}`}
            />
          ) : null}
        </td>
      ) : null}
      <Td>{fmtDateTime(row.requestedOn)}</Td>
      <Td className="truncate max-w-[184px]" title={row.reasonCode?.display}>
        {row.reasonCode?.display ?? '—'}
      </Td>
      <Td className="font-semibold text-text-primary">
        {patientFullName(row.patient)}
      </Td>
      <Td className="text-text-muted">
        {row.patient?.phoneNumber?.trim() || '—'}
      </Td>
      <Td>{row.provider?.name ?? '—'}</Td>
      <Td>
        {row.followUpUnit ? (
          <span>
            {row.followUpUnit}
            <span className="ml-1 text-text-muted">
              ({fmtDate(row.followUpIn)})
            </span>
          </span>
        ) : (
          fmtDate(row.followUpIn)
        )}
      </Td>
      <Td className="text-text-muted">
        {row.lastReminder ? fmtDateTime(row.lastReminder) : '—'}
      </Td>
      <Td>{renderStatus(row.status)}</Td>
      {kebabEnabled ? (
        <td className="px-2 py-2 text-right">
          {showKebab ? (
            <RequestKebab
              row={row}
              busy={kebabBusy}
              onCancelRequest={onCancelRequest}
              onSendReminder={onSendReminder}
              onCreateFollowUp={onCreateFollowUp}
              canCreateFollowUp={canCreateFollowUp}
            />
          ) : null}
        </td>
      ) : null}
    </tr>
  );
}

function RequestKebab({
  row,
  busy,
  onCancelRequest,
  onSendReminder,
  onCreateFollowUp,
  canCreateFollowUp,
}: {
  row: FuRequestResponse;
  busy: boolean;
  onCancelRequest: (row: FuRequestResponse) => void;
  onSendReminder: (row: FuRequestResponse) => void;
  onCreateFollowUp?: (row: FuRequestResponse) => void;
  canCreateFollowUp: boolean;
}) {
  // PROD-920 — render "Create follow up" only when BOTH the parent supplied
  // a handler AND the user has `Doctor_Calender` (add) permission.
  const showCreateFollowUp = !!onCreateFollowUp && canCreateFollowUp;
  // Order matches Flutter (`fu_requests_tab.dart:461-516`): Create · Remind · Cancel.
  const actions: RowAction[] = [];
  if (showCreateFollowUp) {
    actions.push({ label: 'Create follow up', icon: <CalendarPlus className="size-4" aria-hidden />, onClick: () => onCreateFollowUp?.(row), disabled: busy });
  }
  actions.push({ label: 'Send reminder', icon: <Send className="size-4" aria-hidden />, onClick: () => onSendReminder(row), disabled: busy });
  actions.push({ label: 'Cancel request', icon: <X className="size-4" aria-hidden />, onClick: () => onCancelRequest(row), disabled: busy, destructive: true });
  return <RowActions actions={actions} label={`request ${row.id}`} />;
}

function SortableTh({
  column,
  sort,
  onSortColumn,
  children,
}: {
  column: SortColumn;
  sort: SortState | null;
  onSortColumn: (col: SortColumn) => void;
  children: React.ReactNode;
}) {
  const active = sort?.column === column;
  const Icon = !active
    ? ArrowUpDown
    : sort?.direction === 'asc'
      ? ArrowUp
      : ArrowDown;
  return (
    <th className="text-left">
      <button
        type="button"
        onClick={() => onSortColumn(column)}
        className={cn(
          'inline-flex items-center gap-1 hover:text-text-primary transition-colors',
          active && 'text-brand-primary',
        )}
      >
        {children}
        <Icon className="h-3 w-3" aria-hidden />
      </button>
    </th>
  );
}

// ── States ──────────────────────────────────────────────────────────────

function LoadingTable() {
  return (
    <div className="p-6 space-y-3">
      <Skeleton className="h-5 w-1/3" />
      {Array.from({ length: 6 }).map((_, i) => (
        <Skeleton key={i} className="h-12 w-full" />
      ))}
    </div>
  );
}

function ErrorState() {
  return (
    <div className="p-12 text-center">
      <AlertCircle className="h-8 w-8 text-[var(--status-bad)] mx-auto" />
      <p className="mt-2 text-sm font-semibold text-[var(--status-bad)]">
        Couldn't load follow-up requests
      </p>
      <p className="mt-1 [font:var(--type-body)] text-[var(--ink-meta)]">
        Try refreshing the page. If the issue persists, contact support.
      </p>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="p-12 text-center">
      <p className="text-sm font-semibold text-text-primary">
        No requests found
      </p>
      <p className="mt-1 [font:var(--type-body)] text-[var(--ink-meta)]">
        Try changing the search query or the active/cancelled toggle.
      </p>
    </div>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────

function Td({
  children,
  className,
  title,
}: {
  children: React.ReactNode;
  className?: string;
  title?: string;
}) {
  return (
    <td
      title={title}
      className={cn('text-text-primary align-middle', className)}
    >
      {children}
    </td>
  );
}

/**
 * Compare two rows on a sortable column. Returns negative if `a < b`,
 * positive if `a > b`, zero if equal. Direction is applied by the
 * caller (multiply by ±1). Nullable fields sort to the end of asc.
 */
function compareRows(
  a: FuRequestResponse,
  b: FuRequestResponse,
  column: SortColumn,
): number {
  switch (column) {
    case 'requestedOn':
      return cmpDate(a.requestedOn, b.requestedOn);
    case 'reasonCode':
      return cmpString(a.reasonCode?.display, b.reasonCode?.display);
    case 'patient':
      return cmpString(patientFullName(a.patient), patientFullName(b.patient));
    case 'contactNumber':
      return cmpString(a.patient?.phoneNumber, b.patient?.phoneNumber);
    case 'provider':
      return cmpString(a.provider?.name, b.provider?.name);
    case 'followUpIn':
      return cmpDate(a.followUpIn, b.followUpIn);
    case 'lastReminder':
      return cmpDate(a.lastReminder, b.lastReminder);
    case 'status':
      return cmpString(a.status, b.status);
    default:
      return 0;
  }
}

function cmpString(a: string | null | undefined, b: string | null | undefined): number {
  const av = (a ?? '').trim().toLowerCase();
  const bv = (b ?? '').trim().toLowerCase();
  // Empty values sort to the end on ascending (multiply by direction in caller).
  if (!av && !bv) return 0;
  if (!av) return 1;
  if (!bv) return -1;
  return av.localeCompare(bv);
}

function cmpDate(a: string | null | undefined, b: string | null | undefined): number {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  const at = new Date(a).getTime();
  const bt = new Date(b).getTime();
  if (Number.isNaN(at) && Number.isNaN(bt)) return 0;
  if (Number.isNaN(at)) return 1;
  if (Number.isNaN(bt)) return -1;
  return at - bt;
}

function patientFullName(p: FuRequestResponse['patient']): string {
  if (!p) return '—';
  // PatientResponse was widened 2026-05-20 (PR #268 Samer review §5)
  // to include the optional pre-composed `name` and `middleName` that
  // the FU response ships — so no `as unknown as` cast needed.
  if (p.name?.trim()) return p.name.trim();
  const parts = [p.firstName, p.middleName, p.lastName]
    .map((s) => s?.trim() ?? '')
    .filter((s) => s.length > 0);
  return parts.join(' ') || '—';
}

function fmtDate(iso?: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

function fmtDateTime(iso?: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      day: '2-digit',
      month: 'short',
      hour: getTimeFormat() === '24h' ? '2-digit' : 'numeric',
      minute: '2-digit',
      hour12: getTimeFormat() === '12h',
    });
  } catch {
    return iso;
  }
}

/**
 * Render the per-row status as the one soft status chip, like every other table (Antonin, 13 Oct: "calendar fu
 * request status are not consistent, please use the soft chips"). Completed is success, Requested waits on someone
 * (warning), Cancelled is neutral. Unknown statuses stay a NEUTRAL chip with the raw label rather than falling through
 * to a positive colour (Samer PR #266 review §4).
 */
function renderStatus(status: string | null | undefined) {
  const raw = (status ?? '').trim();
  if (!raw) {
    return <span className="text-xs text-text-muted">—</span>;
  }
  const c = raw.toLowerCase();
  const semantic =
    c === 'completed' || c === 'fulfilled' ? 'success'
    : c === 'requested' ? 'warning'
    : c === 'cancelled' || c === 'canceled' ? 'neutral'
    : null;
  if (semantic) return <BridgeStatusChip semantic={semantic}>{raw}</BridgeStatusChip>;
  return (
    <BridgeStatusChip semantic="neutral" title={`Unmapped status: ${raw}`}>
      {raw}
    </BridgeStatusChip>
  );
}
